import { useQuery } from '@tanstack/react-query';
import {
  apartmentChannelsQuery,
  PROVIDERS,
  type ApartmentChannelMapping,
} from '@/lib/channels';
import { ApiError } from '@/lib/api';
import { SyncErrors } from './apartment/SyncErrors';
import { ChannelCard } from './channels/ChannelCard';

interface Props {
  apartmentID: string;
  canWrite: boolean;
}

export function ApartmentChannels({ apartmentID, canWrite }: Props) {
  const { data, error, isLoading } = useQuery(apartmentChannelsQuery(apartmentID));
  const mappings = data?.items ?? [];

  const byProvider = new Map<string, ApartmentChannelMapping>();
  for (const m of mappings) byProvider.set(m.provider, m);

  const errMsg =
    error instanceof ApiError ? error.message : error ? String(error) : null;

  return (
    <section className="mt-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
        OTA channels
      </h2>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {errMsg && (
        <p
          role="alert"
          className="mb-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          {errMsg}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {PROVIDERS.map((p) => (
          <ChannelCard
            key={p}
            provider={p}
            apartmentID={apartmentID}
            mapping={byProvider.get(p)}
            canWrite={canWrite}
          />
        ))}
      </div>

      {/* Everything the cards above don't account for: rows with no resolved
          provider, plus rows attributed to a provider whose card carries no
          list of its own. See OtherSyncErrors for why both kinds land here.
          Renders nothing when there's no residual. */}
      <SyncErrors apartmentID={apartmentID} />
    </section>
  );
}
